// hooks/useCynths.js
/* global BigInt */
// Cynths CDP: users lock collateral (USDT, CRANNI) in positions and mint one synth against it. Debt is owed in
// synth units, stability pools (one per synth) absorb liquidations, and only staked NFT nodes can liquidate.
import { useCallback } from 'react';
import { useWriteContract, usePublicClient } from 'wagmi';
import { ethers } from 'ethers';
import { useAppKitAccount } from '@reown/appkit/react';
import lbDialog from '../components/UI/Modals/lbDialog';
import { maxUint256 } from 'viem';
import { cdpAddress, nftNodesCFG, provider, chainId, synthDexPair } from '../.config';
import cdpAbi from '../abi/CynthsCDP.json';
import poolAbi from '../abi/CynthsStabilityPool.json';
import oracleAbi from '../abi/CynthsOracle.json';
import erc20abi from '../abi/ERC20_ABI.json';
import nodesCfgAbi from '../abi/luckBlocksNodesCFG.json';
import { useT } from '../i18n';

const { parseBytes32String } = ethers.utils;
const ERC20_READ = [
  'function balanceOf(address) view returns (uint256)',
  'function allowance(address,address) view returns (uint256)',
  'function symbol() view returns (string)',
];
const PRICE_ORACLE = ['function price() view returns (uint256)'];
const V2_PAIR = [
  'function getReserves() view returns (uint112 reserve0, uint112 reserve1, uint32 blockTimestampLast)',
  'function token0() view returns (address)',
];
const ZERO = ethers.constants.AddressZero;
// local hardhat only mines on new transactions
const CONFIRMATIONS = chainId === 31337 ? 1 : 2;
// how many of the newest positions the liquidation scan checks
const SCAN_LIMIT = 500;
const SCAN_CHUNK = 25;

/** Resolves to `fallback` instead of throwing: oracles revert while a price is stale or not yet available */
const settle = (promise, fallback = null) => Promise.resolve(promise).catch(() => fallback);

const cdpRead = () => new ethers.Contract(cdpAddress, cdpAbi, provider);
const erc20Read = (address) => new ethers.Contract(address, ERC20_READ, provider);

/** Price (USD, 18 decimals, USDT taken as $1) and USDT depth of a synth/USDT V2 pair; null without a pair */
const loadDexMarket = async (pairAddress, synthToken) => {
  if (!pairAddress) return null;
  const pair = new ethers.Contract(pairAddress, V2_PAIR, provider);
  const [[r0, r1], token0] = await Promise.all([pair.getReserves(), pair.token0()]);
  const [synthReserve, usdtReserve] = token0.toLowerCase() === synthToken.toLowerCase() ? [r0, r1] : [r1, r0];
  if (synthReserve.isZero()) return null;
  return { pair: pairAddress, price: usdtReserve.mul(ethers.BigNumber.from(10).pow(30)).div(synthReserve), usdtReserve };
};

/** Everything about the CDP that does not depend on the connected wallet */
export const loadMarket = async () => {
  const cdp = cdpRead();
  const [
    synthIds,
    collateralAddrs,
    mintFeeBps,
    cranniPremium,
    minDebtUsd,
    goldenShareBps,
    nodeShareBps,
    goldenPoolShareBps,
    poolShareBps,
    oracleAddr,
    cranni,
  ] = await Promise.all([
    cdp.getSynthList(),
    cdp.getCollateralList(),
    cdp.mintFeeBps(),
    cdp.cranniPremium(),
    cdp.minDebtUsd(),
    cdp.goldenPenaltyShareBps(),
    cdp.liquidatorPenaltyShareBps(),
    // CDPs deployed before the pool/own-synths split pay the same share on both paths
    settle(cdp.goldenPoolPenaltyShareBps()),
    settle(cdp.poolPenaltyShareBps()),
    cdp.synthOracle(),
    cdp.cranni(),
  ]);
  const oracle = new ethers.Contract(oracleAddr, oracleAbi, provider);

  const synths = await Promise.all(
    synthIds.map(async (id) => {
      const [token, price, totalDebt, debtCeiling, mintingEnabled, pool, badDebt, isBasket] = await Promise.all([
        cdp.synthTokens(id),
        settle(cdp.synthPrice(id)),
        cdp.totalDebt(id),
        cdp.debtCeiling(id),
        cdp.synthMintingEnabled(id),
        cdp.stabilityPools(id),
        cdp.badDebt(id),
        settle(oracle.isBasket(id), false),
      ]);
      let basket = null;
      if (isBasket) {
        const b = await settle(oracle.getBasket(id));
        if (b) basket = { components: b[0].map(parseBytes32String), amounts: b[1], constantUsd: b[2] };
      }
      let poolStats = null;
      if (pool !== ZERO) {
        const sp = new ethers.Contract(pool, poolAbi, provider);
        const [totalValue, totalShares, synthBalance] = await Promise.all([
          settle(sp.totalValue()),
          sp.totalShares(),
          erc20Read(token).balanceOf(pool),
        ]);
        poolStats = { totalValue, totalShares, synthBalance };
      }
      const ticker = parseBytes32String(id);
      const dex = await settle(loadDexMarket(synthDexPair(ticker), token));
      return { id, ticker, token, price, totalDebt, debtCeiling, mintingEnabled, pool, poolStats, badDebt, basket, dex };
    })
  );

  const collaterals = await Promise.all(
    collateralAddrs.map(async (address) => {
      const cfg = await cdp.collaterals(address);
      const [symbol, price, locked] = await Promise.all([
        settle(erc20Read(address).symbol(), '?'),
        settle(new ethers.Contract(cfg[5], PRICE_ORACLE, provider).price()),
        erc20Read(address).balanceOf(cdpAddress),
      ]);
      return {
        address,
        symbol,
        enabled: cfg[0],
        decimals: cfg[1],
        mcrBps: cfg[2],
        liquidationRatioBps: cfg[3],
        liquidationPenaltyBps: cfg[4],
        price,
        locked,
      };
    })
  );

  const [btcWeight, goldWeight] = await Promise.all([settle(oracle.btcWeight()), settle(oracle.goldWeight())]);

  return {
    synths,
    collaterals,
    mintFeeBps,
    cranniPremium,
    minDebtUsd,
    goldenShareBps,
    nodeShareBps,
    goldenPoolShareBps: goldenPoolShareBps ?? goldenShareBps,
    poolShareBps: poolShareBps ?? nodeShareBps,
    cranni,
    bold: btcWeight && goldWeight ? { btcWeight: btcWeight.toNumber(), goldWeight: goldWeight.toNumber() } : null,
  };
};

const loadPosition = async (cdp, id) => {
  const p = await cdp.positions(id);
  const [ratioBps, maxMintable, liquidatable] = await Promise.all([
    settle(cdp.collateralRatioBps(id)),
    settle(cdp.maxMintable(id)),
    settle(cdp.isLiquidatable(id), false),
  ]);
  return {
    id: ethers.BigNumber.from(id).toNumber(),
    owner: p.owner,
    collateral: p.collateral,
    synth: p.synth,
    collateralAmount: p.collateralAmount,
    debt: p.debt,
    ratioBps,
    maxMintable,
    liquidatable,
  };
};

/** Positions, balances and pool shares of `account` */
export const loadAccount = async (account, market) => {
  const cdp = cdpRead();
  const ids = await cdp.getOwnerPositions(account);
  const positions = (await Promise.all(ids.map((id) => loadPosition(cdp, id))))
    // closed positions keep their record with nothing in it
    .filter((p) => !p.collateralAmount.isZero() || !p.debt.isZero())
    .sort((a, b) => b.id - a.id);

  const tokens = [...market.synths.map((s) => s.token), ...market.collaterals.map((c) => c.address), market.cranni];
  const balanceList = await Promise.all(tokens.map((t) => settle(erc20Read(t).balanceOf(account), ethers.constants.Zero)));
  const balances = Object.fromEntries(tokens.map((t, i) => [t.toLowerCase(), balanceList[i]]));

  const pools = {};
  await Promise.all(
    market.synths
      .filter((s) => s.pool !== ZERO)
      .map(async (s) => {
        const sp = new ethers.Contract(s.pool, poolAbi, provider);
        const [shares, preview] = await Promise.all([sp.shares(account), settle(sp.previewWithdraw(account))]);
        pools[s.ticker] = {
          shares,
          synthOut: preview ? preview[0] : ethers.constants.Zero,
          gains: preview ? preview[1].map((token, i) => ({ token, amount: preview[2][i] })).filter((g) => !g.amount.isZero()) : [],
        };
      })
  );

  return { positions, balances, pools };
};

/** Staked node ids of `account` in the nodes config (only those can liquidate) */
export const loadStakedNodes = async (account) => {
  const cfg = new ethers.Contract(nftNodesCFG, nodesCfgAbi.abi, provider);
  const ids = await settle(cfg.getStakedNodesFromUser(account), []);
  return ids.map(Number).filter((id) => id > 0);
};

/** Liquidatable positions among the newest SCAN_LIMIT ones */
export const scanLiquidatable = async () => {
  const cdp = cdpRead();
  const next = (await cdp.nextPositionId()).toNumber();
  const ids = [];
  for (let id = next - 1; id >= Math.max(1, next - SCAN_LIMIT); id--) ids.push(id);

  const flagged = [];
  for (let i = 0; i < ids.length; i += SCAN_CHUNK) {
    const chunk = ids.slice(i, i + SCAN_CHUNK);
    const results = await Promise.all(chunk.map((id) => settle(cdp.isLiquidatable(id), false)));
    chunk.forEach((id, j) => results[j] && flagged.push(id));
  }
  return Promise.all(flagged.map((id) => loadPosition(cdp, id)));
};

export const useCynths = () => {
  const t = useT();
  const { address } = useAppKitAccount();
  const publicClient = usePublicClient();
  const { writeContractAsync } = useWriteContract();

  const showTransactionDialog = useCallback(
    async (transactionPromise, messages) => {
      try {
        lbDialog.fire({
          title: messages.pending.title,
          html: messages.pending.text,
          icon: 'info',
          showConfirmButton: false,
          allowOutsideClick: false,
          didOpen: () => lbDialog.showLoading(),
        });

        const hash = await transactionPromise;
        const receipt = await publicClient.waitForTransactionReceipt({ hash, confirmations: CONFIRMATIONS });
        if (receipt.status !== 'success') {
          throw new Error('Transaction failed on chain');
        }

        lbDialog.close();
        lbDialog.fire({
          title: messages.success.title,
          text: messages.success.text,
          icon: 'success',
          timer: 2500,
          showConfirmButton: false,
        });
        return receipt;
      } catch (error) {
        lbDialog.close();
        lbDialog.fire({
          title: t('cynths.txFailed'),
          html: `<div class="text-red-300">${error.shortMessage || error.message}</div>`,
          icon: 'error',
          confirmButtonText: 'OK',
        });
        error.handled = true;
        throw error;
      }
    },
    [publicClient, t]
  );

  const ensureAllowance = useCallback(
    async (token, spender, amount) => {
      const allowance = await erc20Read(token).allowance(address, spender);
      if (allowance.gte(amount)) return;
      await showTransactionDialog(
        writeContractAsync({ address: token, abi: erc20abi.abi, functionName: 'approve', args: [spender, maxUint256] }),
        {
          pending: { title: t('cynths.approving'), text: t('cynths.waitApproval') },
          success: { title: t('cynths.approved'), text: t('cynths.approvalSuccess') },
        }
      );
    },
    [address, showTransactionDialog, writeContractAsync, t]
  );

  /** Sends a CDP / pool write and shows the progress dialog */
  const send = useCallback(
    (contract, abi, functionName, args, successText) =>
      // every argument here is a uint (ids, amounts, node ids)
      showTransactionDialog(writeContractAsync({ address: contract, abi, functionName, args: args.map((a) => BigInt(a.toString())) }), {
        pending: { title: t('cynths.sending'), text: t('cynths.waitConfirm') },
        success: { title: t('cynths.done'), text: successText },
      }),
    [showTransactionDialog, writeContractAsync, t]
  );

  const requireWallet = () => {
    if (!address) throw new Error(t('cynths.connectFirst'));
    if (!cdpAddress) throw new Error('CDP address not configured (REACT_APP_CDP_ADDRESS)');
  };

  return {
    address,

    openPosition: async (collateral, synthId, collateralAmount, mintAmount) => {
      requireWallet();
      await ensureAllowance(collateral, cdpAddress, collateralAmount);
      return showTransactionDialog(
        writeContractAsync({
          address: cdpAddress,
          abi: cdpAbi,
          functionName: 'openPosition',
          args: [collateral, synthId, BigInt(collateralAmount.toString()), BigInt(mintAmount.toString())],
        }),
        {
          pending: { title: t('cynths.opening'), text: t('cynths.waitConfirm') },
          success: { title: t('cynths.done'), text: t('cynths.openSuccess') },
        }
      );
    },

    depositCollateral: async (id, collateral, amount) => {
      requireWallet();
      await ensureAllowance(collateral, cdpAddress, amount);
      return send(cdpAddress, cdpAbi, 'depositCollateral', [id, amount], t('cynths.depositSuccess'));
    },

    withdrawCollateral: async (id, amount) => {
      requireWallet();
      return send(cdpAddress, cdpAbi, 'withdrawCollateral', [id, amount], t('cynths.withdrawSuccess'));
    },

    mintSynth: async (id, amount) => {
      requireWallet();
      return send(cdpAddress, cdpAbi, 'mintSynth', [id, amount], t('cynths.mintSuccess'));
    },

    // the CDP burns the synths with burnFrom, so it needs an allowance on the synth token
    repay: async (id, synthToken, amount) => {
      requireWallet();
      await ensureAllowance(synthToken, cdpAddress, amount);
      return send(cdpAddress, cdpAbi, 'repay', [id, amount], t('cynths.repaySuccess'));
    },

    closePosition: async (id, synthToken, debt) => {
      requireWallet();
      if (!debt.isZero()) await ensureAllowance(synthToken, cdpAddress, debt);
      return send(cdpAddress, cdpAbi, 'closePosition', [id], t('cynths.closeSuccess'));
    },

    poolDeposit: async (pool, synthToken, amount) => {
      requireWallet();
      await ensureAllowance(synthToken, pool, amount);
      return send(pool, poolAbi, 'deposit', [amount], t('cynths.poolDepositSuccess'));
    },

    poolWithdraw: async (pool, shares) => {
      requireWallet();
      return send(pool, poolAbi, 'withdraw', [shares], t('cynths.poolWithdrawSuccess'));
    },

    liquidate: async (id, nodeId, synthToken, maxRepay) => {
      requireWallet();
      await ensureAllowance(synthToken, cdpAddress, maxRepay);
      return send(cdpAddress, cdpAbi, 'liquidate', [id, nodeId, maxRepay], t('cynths.liquidateSuccess'));
    },

    liquidateWithPool: async (id, nodeId) => {
      requireWallet();
      return send(cdpAddress, cdpAbi, 'liquidateWithPool', [id, nodeId], t('cynths.liquidateSuccess'));
    },
  };
};
